import { useEffect } from 'react';
import { useToast } from '@common/toast';
import { useSaveKcbIdentity, useCheckIdentity } from '@repository/identity/useIdentity';
import { useUserInfo } from '@repository/auth/useUserInfo';
import KcbSmsVerificationRs from '@domain/rs/verification/KcbSmsVerificationRs';
import KcbIpinVerificationRs from '@domain/rs/verification/KcbIpinVerificationRs';
import { baseURL } from '@utils/common';
import AuthUtil from '@utils/AuthUtil';

interface useKcbIdentityProps {
  onError?: () => void;
  onSuccess?: () => void;
}

const useKcbIdentity = ({ onError, onSuccess }: useKcbIdentityProps) => {
  const Toast = useToast();
  const { data: userInfo } = useUserInfo();
  const isLogin = !!userInfo?.id;
  const { data: isVerification, refetch } = useCheckIdentity(isLogin);
  const { mutateAsync: saveKcbIdentityMutateAsync } = useSaveKcbIdentity((err) => {
    const errMessage = err.response?.data.message ?? '본인인증 실패하였습니다.';
    onError?.();
    Toast({ type: 'error', iconType: 'info', content: errMessage });
    return;
  });

  useEffect(() => {
    //본인 인증 완료시 이벤트 리스너 등록
    window.addEventListener('message', handleVerificationEvent, false);
    return () => window.removeEventListener('message', handleVerificationEvent);
  }, []);

  //본인인증 관련 child window 에서 넘어오는 완료 상태값 처리
  const handleVerificationEvent = async (event: MessageEvent) => {
    try {
      // 신뢰할 수 없는 origin 처리
      if (baseURL !== event.origin) return;
      const verificationRs: KcbSmsVerificationRs | KcbIpinVerificationRs = JSON.parse(event.data);
      //ipin 인증 성공 : T000, sms 인증 성공 : B000
      if (!['T000', 'B000'].includes(verificationRs.code)) {
        onError?.();
        Toast({ type: 'error', iconType: 'info', content: '본인인증 실패하였습니다.' });
        return;
      }
      const result = await saveKcbIdentityMutateAsync(verificationRs);
      if (result !== true) {
        onError?.();
        Toast({ type: 'error', iconType: 'info', content: '본인인증 실패하였습니다.' });
        return;
      }
      //본인 인증 완료 후 브라우저 내 사용자 정보도 갱신
      await AuthUtil.checkAuthorization();
      //본인인증 여부 갱신
      await refetch();
      onSuccess?.(); // 열려있는 본인인증 모달 닫기 처리
    } catch (e) {
      console.error(e);
    }
  };

  return {
    isVerification,
  };
};

export default useKcbIdentity;
